import { makeAutoObservable } from 'mobx';
import { PaginationConfigType } from 'src/domains/casino/webview/bonus-engine/common/components/pagination-table/PaginationTable';
import { EligibleUsersState } from 'src/domains/casino/webview/bonus-engine/campaign/player-management/eligible-users/EligibleUsers.state';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { z } from 'zod';

const pageSizeOptions: number[] = [10, 25, 50, 100];

interface SelectValueType {
    value: string;
    label: string;
    onClick: () => void;
}

const EligibleUsersZod = z.array(z.number());
export type EligibleUsersType = z.TypeOf<typeof EligibleUsersZod>;

export class EligibleUsersTableState {
    private static instance: EligibleUsersTableState | null = null;

    public filter: FormInputState<string, string> = FormInputState.new('');
    public sortConfig: { direction: 'ascending' | 'descending' } | null = null;
    public currentPage: number = 1;
    public rowsPerPage: number = 10;
    public rowsPerPageState = FormInputState.new('');

    public constructor() {
        if (EligibleUsersTableState.instance !== null) {
            return EligibleUsersTableState.instance;
        }

        this.setRowsPerPage(this.rowsPerPage);

        EligibleUsersTableState.instance = this;

        makeAutoObservable(this);
    }

    public setFilter = (value: string): void => {
        this.filter.setValue(value);
        this.currentPage = 1;
    };

    public setSortConfig = (): void => {
        if (this.sortConfig !== null && this.filteredData.length > 0) {
            this.sortConfig.direction = this.sortConfig.direction === 'ascending' ? 'descending' : 'ascending';
        } else {
            this.sortConfig = { direction: 'ascending' };
        }
    };

    public setPage = (page: number): void => {
        this.currentPage = Math.max(1, Math.min(page, this.totalPages));
    };

    public setRowsPerPage = (rows: number): void => {
        this.rowsPerPage = rows;
        this.rowsPerPageState.setValue(rows.toString());
        this.currentPage = 1;
    };

    public get selectValues(): SelectValueType[] {
        return pageSizeOptions.map((size) => ({
            value: size.toString(),
            label: size.toString(),
            onClick: () => this.setRowsPerPage(size),
        }));
    }

    private filterPlayerIds(data: EligibleUsersType): EligibleUsersType {
        const filterText = this.filter.value.toLowerCase();

        if (filterText !== '') {
            data = data.filter((playerId) => {
                return String(playerId).includes(filterText);
            });
        }

        return data;
    }

    private sortPlayerIds(data: EligibleUsersType): EligibleUsersType {
        if (this.sortConfig === null) {
            return data;
        }

        const { direction } = this.sortConfig;

        return [...data].sort((a, b) => {
            const getValue = (playerId: number): string | number | undefined => {
                const value = playerId;

                return typeof value === 'number' ? value : undefined;
            };

            const aValue = getValue(a);
            const bValue = getValue(b);

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return direction === 'ascending' ? aValue - bValue : bValue - aValue;
            }

            return 0;
        });
    }

    public get totalItems(): EligibleUsersType {
        return EligibleUsersState.get();
    }

    public get totalItemsView(): string {
        return new Intl.NumberFormat('en-US').format(this.totalItems.length);
    }

    public get filteredData(): EligibleUsersType {
        const ids = this.totalItems;
        const filteredIds = this.filterPlayerIds(ids);

        return this.sortPlayerIds(filteredIds);
    }

    public get paginatedData(): EligibleUsersType {
        const data = this.filteredData;

        const startIndex = (this.currentPage - 1) * this.rowsPerPage;
        return data.slice(startIndex, startIndex + this.rowsPerPage);
    }

    public get totalPages(): number {
        return Math.max(1, Math.ceil(this.filteredData.length / this.rowsPerPage));
    }

    public get totalFilteredItems(): number {
        return this.filteredData.length;
    }

    public get startItem(): number {
        if (this.totalFilteredItems === 0) {
            return 0;
        }
        return (this.currentPage - 1) * this.rowsPerPage + 1;
    }

    public get endItem(): number {
        return Math.min(this.startItem + this.rowsPerPage - 1, this.totalFilteredItems);
    }

    public get paginationConfig(): PaginationConfigType<string> {
        return {
            currentPage: this.currentPage,
            endItem: this.endItem,
            setPage: this.setPage,
            startItem: this.startItem,
            totalItems: this.totalFilteredItems,
            totalPages: this.totalPages,
            rowsPerPageState: this.rowsPerPageState,
            selectValues: this.selectValues,
        };
    }

    public clearFilters = (): void => {
        this.setFilter('');
        this.sortConfig = null;
        this.setRowsPerPage(10);
    };
}
